/**
 * The scalar rules of the backend's YAML reader — PyYAML, YAML 1.1 — for the
 * `yaml` library. Without these the two disagree on real corpus values
 * (docs/inventory/extractor-grammar.md, W5.0):
 *
 *   value          PyYAML       yaml@2 (1.1 schema)
 *   0059           str          int 59      ← a live mapping key (sophos)
 *   0102043039     str          int         ← a live mapping key (fortigate)
 *   .              str          float NaN   ← the create template's master filter
 *   y / n          str          bool
 *   1e3            str          float
 *   yes / on       bool         bool
 *   0123           int (octal)  int (octal)
 *   1:30           int 90       int 90
 *
 * The model must read a value the way the backend will, and write it so the
 * backend reads it back unchanged. So: the 1.1 tag set with its bool, int and
 * float tags replaced by PyYAML's resolver regexes. Everything else (null,
 * timestamp, binary, sets, …) stays the library's.
 */

import type { Schema, SchemaOptions } from 'yaml'

type Tag = Schema['tags'][number]
type ScalarTag = Extract<Tag, { test?: RegExp }>
/** The library's `customTags` function shape: tag objects or built-in tag ids. */
type Tags = Parameters<Extract<NonNullable<SchemaOptions['customTags']>, (tags: never) => unknown>>[0]

const REPLACED = new Set(['tag:yaml.org,2002:bool', 'tag:yaml.org,2002:int', 'tag:yaml.org,2002:float'])

const boolTag: ScalarTag = {
  identify: (v) => typeof v === 'boolean',
  default: true,
  tag: 'tag:yaml.org,2002:bool',
  test: /^(?:yes|Yes|YES|no|No|NO|true|True|TRUE|false|False|FALSE|on|On|ON|off|Off|OFF)$/,
  resolve: (str) => /^(?:yes|Yes|YES|true|True|TRUE|on|On|ON)$/.test(str),
  stringify: ({ value }) => (value ? 'true' : 'false'),
}

function parseInt11(str: string): number {
  const s = str.replace(/_/g, '')
  const sign = s.startsWith('-') ? -1 : 1
  const body = s.replace(/^[-+]/, '')
  if (/^0b/.test(body)) return sign * parseInt(body.slice(2), 2)
  if (/^0x/.test(body)) return sign * parseInt(body.slice(2), 16)
  if (body.includes(':')) return sign * body.split(':').reduce((acc, part) => acc * 60 + Number(part), 0)
  if (/^0[0-7]+$/.test(body)) return sign * parseInt(body, 8)
  return sign * Number(body)
}

/** A number as text, padded to the fraction digits the source had (`1.0` stays `1.0`). */
function numberText(value: unknown, minFractionDigits: number | undefined): string {
  const n = value as number
  if (Number.isNaN(n)) return '.nan'
  if (!Number.isFinite(n)) return n < 0 ? '-.inf' : '.inf'
  let s = String(n)
  if (minFractionDigits && !s.includes('e')) {
    const [w, f = ''] = s.split('.') as [string, string | undefined]
    if (f.length < minFractionDigits) s = `${w}.${f.padEnd(minFractionDigits, '0')}`
  }
  return s
}

const intTag: ScalarTag = {
  identify: (v) => typeof v === 'number' && Number.isInteger(v),
  default: true,
  tag: 'tag:yaml.org,2002:int',
  test: /^(?:[-+]?0b[0-1_]+|[-+]?0[0-7_]+|[-+]?(?:0|[1-9][0-9_]*)|[-+]?0x[0-9a-fA-F_]+|[-+]?[1-9][0-9_]*(?::[0-5]?[0-9])+)$/,
  resolve: (str) => parseInt11(str),
  // An integer-valued float (schema-version 1.0) is identified here first; the fraction digits say so.
  stringify: ({ value, minFractionDigits }) => numberText(value, minFractionDigits),
}

function parseFloat11(str: string): number {
  const s = str.replace(/_/g, '')
  if (/^[-+]?\.(?:inf|Inf|INF)$/.test(s)) return s.startsWith('-') ? -Infinity : Infinity
  if (/^\.(?:nan|NaN|NAN)$/.test(s)) return NaN
  if (s.includes(':')) {
    const sign = s.startsWith('-') ? -1 : 1
    const [whole, frac] = s.replace(/^[-+]/, '').split('.') as [string, string | undefined]
    return sign * (whole.split(':').reduce((acc, part) => acc * 60 + Number(part), 0) + Number(`0.${frac || '0'}`))
  }
  return Number(s)
}

const floatTag: ScalarTag = {
  identify: (v) => typeof v === 'number' && !Number.isInteger(v),
  default: true,
  tag: 'tag:yaml.org,2002:float',
  test: /^(?:[-+]?(?:[0-9][0-9_]*)\.[0-9_]*(?:[eE][-+][0-9]+)?|\.[0-9_]+(?:[eE][-+][0-9]+)?|[-+]?[0-9][0-9_]*(?::[0-5]?[0-9])+\.[0-9_]*|[-+]?\.(?:inf|Inf|INF)|\.(?:nan|NaN|NAN))$/,
  resolve: (str) => parseFloat11(str),
  stringify: ({ value, minFractionDigits }) => numberText(value, minFractionDigits),
}

/** `customTags` for `YAML.parseDocument` / `new YAML.Document` with `version: '1.1'`. */
export function pyyamlTags(tags: Tags): Tags {
  return [boolTag, intTag, floatTag, ...tags.filter((t) => typeof t === 'string' || !REPLACED.has(t.tag))]
}
